'use client';

import * as Sentry from '@sentry/react';
import { User as FirebaseUser } from 'firebase/auth';
import { ReactNode, createContext, useCallback, useEffect, useState } from 'react';

import { toast } from '@/components/ui/hooks/use-toast';
import { axiosClient } from '@/lib/api-client';

import { clearImpersonation } from '@/auth/impersonation';
import { useCsrf } from '@/lib/csrf-context';
import { type LoggedInUser } from '@airreading/common/data';

import { useFirebaseAuth } from './useFirebaseAuth';

export type IdentityProviderUser = FirebaseUser | null | undefined;

export interface AuthContext {
  /**
   * undefined: identity provider hasn't reported yet
   * null: user is not signed in
   * User: user signed in
   *
   */
  identityProviderUser: IdentityProviderUser;
  loggedInUser: LoggedInUser | undefined;
  actingUser: LoggedInUser | undefined;

  login: () => unknown;
  logout: () => unknown;
  loading: boolean;
}

export const AuthContext = createContext<AuthContext>({
  identityProviderUser: undefined,
  loggedInUser: undefined,
  actingUser: undefined,
  login: () => {},
  logout: () => {},
  loading: true,
});

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { identityProviderUser, login: providerLogin, logout: providerLogout } = useFirebaseAuth();
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const { csrfToken, refreshCsrfToken, clearCsrfToken } = useCsrf();

  const airLogin = useCallback(() => {
    return axiosClient.get<LoggedInUser>('/me/login');
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint to revoke session tokens
      await axiosClient.post('/me/logout');
    } catch (error) {
      Sentry.captureException(error);
      // Continue with client logout even if backend fails
    }

    // Clear local state
    clearImpersonation({ reload: false });
    clearCsrfToken();

    // Sign out from the identity provider (client-side)
    return providerLogout();
  }, [clearCsrfToken, providerLogout]);

  useEffect(() => {
    if (csrfToken) {
      axiosClient.defaults.headers['x-csrf-token'] = csrfToken;
    }
  }, [csrfToken]);

  useEffect(() => {
    if (identityProviderUser === undefined) {
      return;
    }

    if (identityProviderUser) {
      refreshCsrfToken().then(() =>
        airLogin()
          .then(async ({ data: user }) => {
            Sentry.setUser({ id: user.id });

            setLoggedInUser(user);
            setLoading(false);
          })
          .catch(() => {
            toast({
              description: `Failed to login or we could not find your account ${identityProviderUser.email}. Please contact Air Reading administrator.`,
            });
            setLoading(false);
            logout();
          }),
      );
    } else {
      setLoading(false);
      setLoggedInUser(undefined);
    }
  }, [identityProviderUser, airLogin, refreshCsrfToken, logout]);

  const login = async () => {
    setLoading(true);
    return providerLogin();
  };

  const authValue = {
    identityProviderUser,
    loggedInUser,
    actingUser: loggedInUser?.impersonatingAs || loggedInUser,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};
